import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit as limitFirestore,
  serverTimestamp,
  Timestamp,
  DocumentData,
  QueryDocumentSnapshot
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  Promotion,
  CreatePromotionData,
  UpdatePromotionData,
  PromotionStatus,
  DiscountType,
  ProductDiscount,
  PromotionScope,
  QuantityCondition
} from '../types/promotion';
import { InventoryItem } from '../utils/types';

// الحصول على مرجع مجموعة العروض
const promotionsCollection = collection(db, 'promotions');

/**
 * الحصول على جميع العروض
 * @returns قائمة بجميع العروض
 */
export const getAllPromotions = async (): Promise<Promotion[]> => {
  try {
    console.log('🔍 بدء تحميل العروض...');

    // التحقق من اتصال قاعدة البيانات
    if (!db) {
      throw new Error('قاعدة البيانات غير متصلة');
    }

    // محاولة الحصول على العروض مع ترتيب
    let q;
    try {
      q = query(promotionsCollection, orderBy('priority', 'desc'));
    } catch (orderError) {
      console.warn('تحذير: فشل في ترتيب العروض، سيتم تحميلها بدون ترتيب:', orderError);
      q = query(promotionsCollection);
    }

    const querySnapshot = await getDocs(q);
    console.log(`✅ تم تحميل ${querySnapshot.size} عرض من قاعدة البيانات`);

    if (querySnapshot.empty) {
      console.log('⚠️ لا توجد عروض في قاعدة البيانات');
      return [];
    }

    const promotions = querySnapshot.docs.map(doc => {
      try {
        const data = doc.data();

        // التحقق من وجود الحقول المطلوبة
        if (!data.title || !data.description) {
          console.warn(`تحذير: عرض بمعرف ${doc.id} يحتوي على بيانات ناقصة`);
        }

        return {
          id: doc.id,
          title: data.title || 'عرض بدون عنوان',
          description: data.description || 'عرض بدون وصف',
          imageUrl: data.imageUrl || '',
          startDate: data.startDate?.toDate() || new Date(),
          endDate: data.endDate?.toDate() || new Date(),
          status: data.status || PromotionStatus.DRAFT,
          scope: data.scope || PromotionScope.ALL,
          discountType: data.discountType || DiscountType.PERCENTAGE,
          discountValue: data.discountValue || { value: 0 },
          conditions: data.conditions || {},
          buyXGetYDetails: data.buyXGetYDetails,
          bundleDetails: data.bundleDetails,
          usageLimit: data.usageLimit,
          usageCount: data.usageCount || 0,
          priority: data.priority || 1,
          isActive: data.isActive !== undefined ? data.isActive : true,
          showInOffers: data.showInOffers !== undefined ? data.showInOffers : true,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as Promotion;
      } catch (docError) {
        console.error(`خطأ في معالجة العرض ${doc.id}:`, docError);
        return null;
      }
    }).filter(promotion => promotion !== null) as Promotion[];

    console.log(`✅ تم معالجة ${promotions.length} عرض بنجاح`);
    return promotions;

  } catch (error) {
    console.error('❌ خطأ في تحميل العروض:', error);

    // تحليل نوع الخطأ وتقديم رسالة مفيدة
    if (error instanceof Error) {
      if (error.message.includes('permission-denied')) {
        throw new Error('ليس لديك صلاحية للوصول إلى العروض. تأكد من تسجيل الدخول بحساب مناسب.');
      } else if (error.message.includes('unavailable')) {
        throw new Error('الخدمة غير متاحة حالياً. تحقق من اتصال الإنترنت وحاول مرة أخرى.');
      } else if (error.message.includes('not-found')) {
        throw new Error('مجموعة العروض غير موجودة في قاعدة البيانات.');
      }
    }

    throw new Error(`حدث خطأ أثناء تحميل العروض: ${error instanceof Error ? error.message : 'خطأ غير معروف'}`);
  }
};

/**
 * الحصول على العروض النشطة
 * @returns قائمة بالعروض النشطة
 */
export const getActivePromotions = async (): Promise<Promotion[]> => {
  try {
    const now = new Date();

    const q = query(
      promotionsCollection,
      where('isActive', '==', true)
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title,
          description: data.description,
          imageUrl: data.imageUrl,
          startDate: data.startDate?.toDate() || new Date(),
          endDate: data.endDate?.toDate() || new Date(),
          status: data.status,
          scope: data.scope,
          discountType: data.discountType,
          discountValue: data.discountValue,
          conditions: data.conditions,
          buyXGetYDetails: data.buyXGetYDetails,
          bundleDetails: data.bundleDetails,
          usageLimit: data.usageLimit,
          usageCount: data.usageCount || 0,
          priority: data.priority,
          isActive: data.isActive,
          showInOffers: data.showInOffers,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as Promotion;
      })
      .filter(p => p.startDate <= now && p.endDate >= now)
      .sort((a, b) => b.priority - a.priority);
  } catch (error) {
    console.error('Error getting active promotions:', error);
    throw error;
  }
};

/**
 * الحصول على العروض التي تظهر في صفحة العروض
 * @param limit عدد العروض المطلوبة
 * @returns قائمة بالعروض
 */
export const getOffersPagePromotions = async (limitCount: number = 10): Promise<Promotion[]> => {
  try {
    const now = new Date();

    const q = query(
      promotionsCollection,
      where('isActive', '==', true),
      where('showInOffers', '==', true)
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title,
          description: data.description,
          imageUrl: data.imageUrl,
          startDate: data.startDate?.toDate() || new Date(),
          endDate: data.endDate?.toDate() || new Date(),
          status: data.status,
          scope: data.scope,
          discountType: data.discountType,
          discountValue: data.discountValue,
          conditions: data.conditions,
          buyXGetYDetails: data.buyXGetYDetails,
          bundleDetails: data.bundleDetails,
          usageLimit: data.usageLimit,
          usageCount: data.usageCount || 0,
          priority: data.priority,
          isActive: data.isActive,
          showInOffers: data.showInOffers,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as Promotion;
      })
      .filter(p => p.startDate <= now && p.endDate >= now)
      .sort((a, b) => b.priority - a.priority)
      .slice(0, limitCount);
  } catch (error) {
    console.error('Error getting offers page promotions:', error);
    throw error;
  }
};

/**
 * الحصول على عرض بواسطة المعرف
 * @param id معرف العرض
 * @returns بيانات العرض
 */
export const getPromotionById = async (id: string): Promise<Promotion | null> => {
  try {
    const promotionDoc = await getDoc(doc(promotionsCollection, id));

    if (!promotionDoc.exists()) {
      return null;
    }

    const data = promotionDoc.data();
    return {
      id: promotionDoc.id,
      title: data.title,
      description: data.description,
      imageUrl: data.imageUrl,
      startDate: data.startDate?.toDate() || new Date(),
      endDate: data.endDate?.toDate() || new Date(),
      status: data.status,
      scope: data.scope,
      discountType: data.discountType,
      discountValue: data.discountValue,
      conditions: data.conditions,
      buyXGetYDetails: data.buyXGetYDetails,
      bundleDetails: data.bundleDetails,
      usageLimit: data.usageLimit,
      usageCount: data.usageCount || 0,
      priority: data.priority,
      isActive: data.isActive,
      showInOffers: data.showInOffers,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    } as Promotion;
  } catch (error) {
    console.error('Error getting promotion by ID:', error);
    throw error;
  }
};

/**
 * إنشاء عرض جديد
 * @param promotionData بيانات العرض الجديد
 * @returns معرف العرض الجديد
 */
export const createPromotion = async (promotionData: CreatePromotionData): Promise<string> => {
  try {
    const docRef = await addDoc(promotionsCollection, {
      ...promotionData,
      usageCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return docRef.id;
  } catch (error) {
    console.error('Error creating promotion:', error);
    throw error;
  }
};

/**
 * تحديث عرض
 * @param id معرف العرض
 * @param promotionData بيانات التحديث
 */
export const updatePromotion = async (id: string, promotionData: UpdatePromotionData): Promise<void> => {
  try {
    const promotionRef = doc(promotionsCollection, id);
    await updateDoc(promotionRef, {
      ...promotionData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating promotion:', error);
    throw error;
  }
};

/**
 * حذف عرض
 * @param id معرف العرض
 */
export const deletePromotion = async (id: string): Promise<void> => {
  try {
    await deleteDoc(doc(promotionsCollection, id));
  } catch (error) {
    console.error('Error deleting promotion:', error);
    throw error;
  }
};

/**
 * زيادة عدد مرات استخدام العرض
 * @param id معرف العرض
 */
export const incrementPromotionUsage = async (id: string): Promise<void> => {
  try {
    const promotionRef = doc(promotionsCollection, id);
    const promotionDoc = await getDoc(promotionRef);

    if (!promotionDoc.exists()) {
      throw new Error('Promotion not found');
    }

    const data = promotionDoc.data();
    const usageCount = data.usageCount || 0;

    await updateDoc(promotionRef, {
      usageCount: usageCount + 1,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error incrementing promotion usage:', error);
    throw error;
  }
};

/**
 * تحديث حالة العرض
 * @param id معرف العرض
 * @param status الحالة الجديدة
 */
export const updatePromotionStatus = async (id: string, status: PromotionStatus): Promise<void> => {
  try {
    const promotionRef = doc(promotionsCollection, id);
    await updateDoc(promotionRef, {
      status,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating promotion status:', error);
    throw error;
  }
};

/**
 * الحصول على المنتجات المشمولة في العرض
 * @param promotion العرض
 * @returns قائمة بمعرفات المنتجات المشمولة في العرض
 */
export const getPromotionProducts = async (promotion: Promotion): Promise<string[]> => {
  try {
    if (promotion.scope === PromotionScope.PRODUCT && promotion.conditions.productIds) {
      return promotion.conditions.productIds;
    } else if (promotion.scope === PromotionScope.CATEGORY && promotion.conditions.categoryIds) {
      // الحصول على المنتجات في الفئات المحددة
      const productIds: string[] = [];

      for (const categoryId of promotion.conditions.categoryIds) {
        const productsQuery = query(
          collection(db, 'inventory'),
          where('categoryId', '==', categoryId),
          where('showInStore', '==', true)
        );

        const productsSnapshot = await getDocs(productsQuery);
        productsSnapshot.docs.forEach(doc => {
          productIds.push(doc.id);
        });

        // البحث أيضًا في الفئات الفرعية
        const subcategoryProductsQuery = query(
          collection(db, 'inventory'),
          where('subcategoryId', '==', categoryId),
          where('showInStore', '==', true)
        );

        const subcategoryProductsSnapshot = await getDocs(subcategoryProductsQuery);
        subcategoryProductsSnapshot.docs.forEach(doc => {
          productIds.push(doc.id);
        });
      }

      return productIds;
    } else if (promotion.scope === PromotionScope.ALL) {
      // الحصول على جميع المنتجات المعروضة في المتجر
      const productsQuery = query(
        collection(db, 'inventory'),
        where('showInStore', '==', true)
      );

      const productsSnapshot = await getDocs(productsQuery);
      return productsSnapshot.docs.map(doc => doc.id);
    }

    return [];
  } catch (error) {
    console.error('Error getting promotion products:', error);
    throw error;
  }
};

/**
 * حساب التخفيض على منتج
 * @param product المنتج
 * @param quantity الكمية
 * @returns تفاصيل التخفيض
 */
export const calculateProductDiscount = async (
  product: InventoryItem,
  quantity: number = 1
): Promise<ProductDiscount | null> => {
  try {
    // الحصول على العروض النشطة
    const activePromotions = await getActivePromotions();

    if (activePromotions.length === 0) {
      return null;
    }

    // تصفية العروض التي تنطبق على المنتج
    const applicablePromotions: Promotion[] = [];

    for (const promotion of activePromotions) {
      if (promotion.scope === PromotionScope.PRODUCT && promotion.conditions.productIds) {
        // التحقق مما إذا كان المنتج مشمولاً في العرض
        if (promotion.conditions.productIds.includes(product.id)) {
          applicablePromotions.push(promotion);
        }
      } else if (promotion.scope === PromotionScope.CATEGORY && promotion.conditions.categoryIds) {
        // التحقق مما إذا كانت فئة المنتج مشمولة في العرض
        if (
          (product.categoryId && promotion.conditions.categoryIds.includes(product.categoryId)) ||
          (product.subcategoryId && promotion.conditions.categoryIds.includes(product.subcategoryId))
        ) {
          applicablePromotions.push(promotion);
        }
      } else if (promotion.scope === PromotionScope.ALL) {
        // العرض يشمل جميع المنتجات
        applicablePromotions.push(promotion);
      }
    }

    if (applicablePromotions.length === 0) {
      return null;
    }

    // ترتيب العروض حسب الأولوية
    applicablePromotions.sort((a, b) => b.priority - a.priority);

    // اختيار العرض الأفضل للمنتج
    let bestPromotion: Promotion | null = null;
    let bestDiscount: number = 0;
    let finalPrice: number = product.sellingPrice;
    let quantityCondition: QuantityCondition | undefined;

    for (const promotion of applicablePromotions) {
      // التحقق من شروط الكمية
      if (promotion.conditions.quantityConditions && promotion.conditions.quantityConditions.length > 0) {
        let matchingCondition = false;

        for (const condition of promotion.conditions.quantityConditions) {
          if (
            quantity >= condition.minQuantity &&
            (!condition.maxQuantity || quantity <= condition.maxQuantity)
          ) {
            matchingCondition = true;
            quantityCondition = condition;
            break;
          }
        }

        if (!matchingCondition) {
          continue; // تخطي هذا العرض إذا لم تتطابق شروط الكمية
        }
      }

      // حساب التخفيض بناءً على نوع التخفيض
      let discountAmount = 0;

      if (promotion.discountType === DiscountType.PERCENTAGE) {
        discountAmount = (product.sellingPrice * promotion.discountValue.value) / 100;

        // التحقق من الحد الأقصى للتخفيض
        if (promotion.discountValue.maxDiscount && discountAmount > promotion.discountValue.maxDiscount) {
          discountAmount = promotion.discountValue.maxDiscount;
        }
      } else if (promotion.discountType === DiscountType.FIXED_AMOUNT) {
        discountAmount = promotion.discountValue.value;
      } else if (promotion.discountType === DiscountType.BUY_X_GET_Y && promotion.buyXGetYDetails) {
        // حساب التخفيض لعرض اشتر X واحصل على Y
        const { buyQuantity, getQuantity } = promotion.buyXGetYDetails;
        const totalQuantity = buyQuantity + getQuantity;
        const sets = Math.floor(quantity / totalQuantity);
        const freeItems = sets * getQuantity;

        if (freeItems > 0) {
          discountAmount = (freeItems * product.sellingPrice) / quantity;
        }
      } else if (promotion.discountType === DiscountType.BUNDLE && promotion.bundleDetails) {
        // حساب التخفيض للحزمة
        // هذا يتطلب معالجة خاصة في سلة التسوق
        continue;
      }

      // التحقق مما إذا كان هذا العرض أفضل من العروض السابقة
      if (discountAmount > bestDiscount) {
        bestDiscount = discountAmount;
        bestPromotion = promotion;
        finalPrice = product.sellingPrice - discountAmount;
      }
    }

    if (bestPromotion && bestDiscount > 0) {
      return {
        promotionId: bestPromotion.id,
        discountType: bestPromotion.discountType,
        discountValue: bestPromotion.discountValue.value,
        originalPrice: product.sellingPrice,
        finalPrice,
        quantityCondition
      };
    }

    return null;
  } catch (error) {
    console.error('Error calculating product discount:', error);
    throw error;
  }
};

/**
 * الحصول على المنتجات التي عليها عروض
 * @param limit عدد المنتجات المطلوبة
 * @param lastVisible آخر منتج تم تحميله (للتحميل المتدرج)
 * @returns قائمة بالمنتجات التي عليها عروض
 */
export const getOnSaleProducts = async (
  limit: number = 10,
  lastVisible?: QueryDocumentSnapshot<DocumentData>
): Promise<InventoryItem[]> => {
  try {
    // الحصول على العروض النشطة
    const activePromotions = await getActivePromotions();

    if (activePromotions.length === 0) {
      return [];
    }

    // جمع معرفات المنتجات المشمولة في العروض
    const productIds: Set<string> = new Set();
    const categoryIds: Set<string> = new Set();
    let hasGlobalPromotion = false;

    for (const promotion of activePromotions) {
      if (promotion.scope === PromotionScope.PRODUCT && promotion.conditions.productIds) {
        promotion.conditions.productIds.forEach(id => productIds.add(id));
      } else if (promotion.scope === PromotionScope.CATEGORY && promotion.conditions.categoryIds) {
        promotion.conditions.categoryIds.forEach(id => categoryIds.add(id));
      } else if (promotion.scope === PromotionScope.ALL) {
        hasGlobalPromotion = true;
        break;
      }
    }

    // إنشاء استعلام للحصول على المنتجات
    let productsQuery;

    if (hasGlobalPromotion) {
      // إذا كان هناك عرض عام، نحصل على جميع المنتجات المعروضة في المتجر
      productsQuery = query(
        collection(db, 'inventory'),
        where('showInStore', '==', true),
        limitFirestore(20) // تحديد عدد النتائج بـ 20
      );
    } else if (productIds.size > 0 || categoryIds.size > 0) {
      // إنشاء استعلامات متعددة ودمج النتائج
      const queries = [];

      if (productIds.size > 0) {
        // تقسيم معرفات المنتجات إلى مجموعات (Firestore تدعم حتى 10 قيم في عامل in)
        const productIdChunks = Array.from(productIds).reduce((resultArray, item, index) => {
          const chunkIndex = Math.floor(index / 10);

          if (!resultArray[chunkIndex]) {
            resultArray[chunkIndex] = [];
          }

          resultArray[chunkIndex].push(item);
          return resultArray;
        }, [] as string[][]);

        for (const chunk of productIdChunks) {
          queries.push(
            query(
              collection(db, 'inventory'),
              where('id', 'in', chunk),
              where('showInStore', '==', true)
            )
          );
        }
      }

      if (categoryIds.size > 0) {
        // تقسيم معرفات الفئات إلى مجموعات
        const categoryIdChunks = Array.from(categoryIds).reduce((resultArray, item, index) => {
          const chunkIndex = Math.floor(index / 10);

          if (!resultArray[chunkIndex]) {
            resultArray[chunkIndex] = [];
          }

          resultArray[chunkIndex].push(item);
          return resultArray;
        }, [] as string[][]);

        for (const chunk of categoryIdChunks) {
          queries.push(
            query(
              collection(db, 'inventory'),
              where('categoryId', 'in', chunk),
              where('showInStore', '==', true)
            )
          );

          queries.push(
            query(
              collection(db, 'inventory'),
              where('subcategoryId', 'in', chunk),
              where('showInStore', '==', true)
            )
          );
        }
      }

      // تنفيذ الاستعلامات وجمع النتائج
      const results: InventoryItem[] = [];

      for (const q of queries) {
        const snapshot = await getDocs(q);

        for (const doc of snapshot.docs) {
          const data = doc.data();

          // التحقق من عدم تكرار المنتجات
          if (!results.some(item => item.id === doc.id)) {
            const product: InventoryItem = {
              id: doc.id,
              name: data.name || '',
              sku: data.sku || '',
              barcode: data.barcode || '',
              category: data.category || '',
              categoryId: data.categoryId || '',
              subcategory: data.subcategory || '',
              subcategoryId: data.subcategoryId || '',
              purchasePrice: data.purchasePrice || 0,
              sellingPrice: data.sellingPrice || 0,
              quantity: data.quantity || 0,
              minQuantity: data.minQuantity || 0,
              description: data.description || '',
              supplier: data.supplier || '',
              image: data.image || '',
              imageUrl: data.imageUrl || data.image || '',
              showInStore: data.showInStore !== undefined ? data.showInStore : true,
              featured: data.featured || false,
              bestSeller: data.bestSeller || false,
              createdAt: data.createdAt?.toDate() || new Date(),
              updatedAt: data.updatedAt?.toDate() || new Date()
            };

            // حساب التخفيض للمنتج
            const discount = await calculateProductDiscount(product);

            if (discount) {
              product.onSale = true;
              product.discount = discount;
              product.originalPrice = product.sellingPrice;
              product.sellingPrice = discount.finalPrice;

              results.push(product);
            }
          }
        }
      }

      // ترتيب النتائج وتحديد العدد المطلوب
      return results.slice(0, limit);
    } else {
      return [];
    }

    // تنفيذ الاستعلام
    const productsSnapshot = await getDocs(productsQuery);

    // تحويل النتائج إلى كائنات منتج مع حساب التخفيضات
    const products: InventoryItem[] = [];

    for (const doc of productsSnapshot.docs) {
      const data = doc.data();

      const product: InventoryItem = {
        id: doc.id,
        name: data.name || '',
        sku: data.sku || '',
        barcode: data.barcode || '',
        category: data.category || '',
        categoryId: data.categoryId || '',
        subcategory: data.subcategory || '',
        subcategoryId: data.subcategoryId || '',
        purchasePrice: data.purchasePrice || 0,
        sellingPrice: data.sellingPrice || 0,
        quantity: data.quantity || 0,
        minQuantity: data.minQuantity || 0,
        description: data.description || '',
        supplier: data.supplier || '',
        image: data.image || '',
        imageUrl: data.imageUrl || data.image || '',
        showInStore: data.showInStore !== undefined ? data.showInStore : true,
        featured: data.featured || false,
        bestSeller: data.bestSeller || false,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      };

      // حساب التخفيض للمنتج
      const discount = await calculateProductDiscount(product);

      if (discount) {
        product.onSale = true;
        product.discount = discount;
        product.originalPrice = product.sellingPrice;
        product.sellingPrice = discount.finalPrice;

        products.push(product);
      }
    }

    return products;
  } catch (error) {
    console.error('Error getting on sale products:', error);
    throw error;
  }
};
